import {ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ViewEncapsulation} from '@angular/core';
import {Router} from '@angular/router';
import {ZXingScannerComponent} from '@zxing/ngx-scanner';
import {AventiObject} from 'src/app/models/AventiObject';
import {firstValueFrom} from 'rxjs';
import {ObjectService} from 'src/app/services/object.service';
import {environment} from 'src/environments/environment';
import {MediaMatcher} from '@angular/cdk/layout';
import {Key, KEY_VALUE} from 'src/app/components/field-scan/types';
import {BarcodeFormat} from '@zxing/library';

@Component({
  selector: 'app-object-qr-search',
  templateUrl: './object-qr-search.component.html',
  styleUrls: ['./object-qr-search.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ObjectQrSearchComponent implements OnInit, OnChanges {
  @ViewChild('scanner') scanner!: ZXingScannerComponent;

  @Input() projectId: string;
  @Input() projectName: string;
  @Input() key: Key;
  @Input() objectsName: AventiObject[];
  @Input() objectsTfm: AventiObject[];
  @Input() objectsRds: AventiObject[];
  @Input() warning: string | null = null;

  info: any | null = null;
  count: number = 0;

  devices: MediaDeviceInfo[] = [];
  currentDevice: MediaDeviceInfo | null = null;
  deviceId: string;

  torchEnabled = false;
  torchAvailable = false;
  isCameraLoading = true;
  permissionDenied = false;
  disableScanner = false;

  allowedFormats = [BarcodeFormat.QR_CODE];

  qrResult: string | null = null;
  searchValue: string | null = null;

  mobileQuery: MediaQueryList;
  isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

  zoomRange?: { min: number; max: number; step: number };
  zoomValue = 1;

  private readonly defaultConstraints: MediaTrackConstraints = {
    facingMode: {ideal: 'environment'},
    width: {ideal: 1280},
    height: {ideal: 720}
  };

  constructor(
    private router: Router,
    media: MediaMatcher,
    private objectservice: ObjectService,
    private cdr: ChangeDetectorRef
  ) {
    this.mobileQuery = media.matchMedia(environment.mobileQueryMaxwidthSmall);
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.objectsName?.currentValue) {
      this.objectsName = changes.objectsName.currentValue;
    }
    if (changes.objectsTfm?.currentValue) {
      this.objectsTfm = changes.objectsTfm.currentValue;
    }
    if (changes.objectsRds?.currentValue) {
      this.objectsRds = changes.objectsRds.currentValue;
    }
  }

  async requestPermission() {
    this.info = 'requestPermission';
    // Try to trigger the browser prompt again (works if user dismissed previously;
    // if "blocked", user must change site permissions manually).
    try {
      const stream = await navigator.mediaDevices.getUserMedia({video: this.defaultConstraints});
      // Close the provisional stream; ZXing will open its own
      stream.getTracks().forEach(t => t.stop());
      this.permissionDenied = false;
      this.warning = null;
      this.disableScanner = true;
      await new Promise(res => setTimeout(res, 100)); // wait a tick
      this.disableScanner = false;
      await new Promise(res => setTimeout(res, 100)); // wait a tick
    } catch (e) {
      // Still denied or blocked
      this.permissionDenied = true;
      this.isCameraLoading = false;
    }
  }

  async onPermission(granted: boolean) {
    if (!granted) {
      this.warning = 'Kameratilgang ble nektet.';
      this.permissionDenied = true;
      this.isCameraLoading = false;
      return;
    }
    this.permissionDenied = false;
    this.warning = null;
  }

  async onCamerasFound(devices: MediaDeviceInfo[]) {
    this.info = 'onCamerasFound';
    this.isCameraLoading = true;
    this.devices = [...devices] ?? [];

// triple-camera modules
    const TRIPLE_RE = /\b(triple|trippel(?:t)?)\b/i;
// dual-camera modules
    const DOUBLE_RE = /\b(dual|duo|dobbel|doble|double|dupla|duplo)\b/i;
// wide / ultrawide lenses
    const WIDE_RE = /\b(ultra[-\s]*wide|ultrawide|wide[-\s]*angle|vidvinkel|wide)\b/i;
// back / main / rear cameras
    const BACK_RE = /\b(back|bak|bakre|rear|environment|main|primary)\b/i;
// front / selfie / user-facing cameras
    const FRONT_RE = /\b(front|foran|selfie|user|frontal|face(?:time)?)\b/i;

    const triple = devices.find(d => TRIPLE_RE.test(d.label || ''));
    const double = devices.find(d => DOUBLE_RE.test(d.label || ''));
    const wide = devices.find(d => WIDE_RE.test(d.label || ''));
    const back = devices.find(d => BACK_RE.test(d.label || ''));
    const front = devices.find(d => FRONT_RE.test(d.label || ''));
    const pick = triple ?? double ?? wide ?? back ?? front ?? devices[0];

    if (pick) {
      setTimeout(async () => {
        await this.mountScannerWithDevice(pick);
      }, 1000);
    }
  }

  private async mountScannerWithDevice(device: MediaDeviceInfo) {
    this.count++;
    this.info = 'mountScannerWithDevice';
    this.isCameraLoading = true;
    this.currentDevice = {...device};   // set our choice first
    this.deviceId = device.deviceId;
    this.cdr.detectChanges();             // flush view (ensures destroy)

    // Remount next tick and only then allow autostart
    setTimeout(() => {
      this.cdr.detectChanges();
      setTimeout(() => {
        this.readCapabilities();
        this.deviceId = device.deviceId;
        this.info = 'mountScannerWithDevice - capabilities read';
        setTimeout(() => {
          this.isCameraLoading = false;
          this.info = 'mountScannerWithDevice - ready';
        }, 50);
      }, 500);
    }, 0);
  }

  readCapabilities() {
    const video = this.scanner?.previewElemRef?.nativeElement as HTMLVideoElement | undefined;
    const track = (video?.srcObject as MediaStream | undefined)?.getVideoTracks?.()[0];
    const caps: any = track?.getCapabilities?.();
    if (caps?.zoom) {
      this.zoomRange = {min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step ?? 0.1};
      this.zoomValue = Math.max(this.zoomRange.min, Math.min(this.zoomRange.max, 1));
    }
  }

  // === Torch / Zoom ===
  onTorchCompatible(isCompatible: boolean) {
    if (!isCompatible) {
      this.torchEnabled = false;
      this.torchAvailable = false;
    } else {
      setTimeout(() => {
        this.torchAvailable = true;
      }, 500);
    }
  }

  toggleTorch() {
    this.torchEnabled = !this.torchEnabled;
  }

  async setZoom(val: number) {
    const video = this.scanner?.previewElemRef?.nativeElement as HTMLVideoElement | undefined;
    const track = (video?.srcObject as MediaStream | undefined)?.getVideoTracks?.()[0];
    if (!track || typeof track.applyConstraints !== 'function') {
      return;
    }
    console.log(val);

    const caps: any = (track as any).getCapabilities?.();
    if (!caps?.zoom) {
      return; // device doesn't support zoom
    }

    const min = caps.zoom.min ?? 0;
    const max = caps.zoom.max ?? 1;
    const clamped = Math.max(min, Math.min(max, val));

    try {
      // Try the simple constraint first
      await track.applyConstraints({zoom: clamped} as any);
    } catch (e) {
      try {
        // Fallback to advanced form if the browser requires it
        await track.applyConstraints({advanced: [{zoom: clamped}]} as any);
      } catch (err) {
        console.warn('applyConstraints for zoom failed', err);
        return;
      }
    }

    this.zoomValue = clamped;
  }

  // === Scan handler ===
  async onCodeResult(result: string) {
    this.qrResult = result;
    this.searchValue = null;
    this.warning = null;
    try {
      const parsed = JSON.parse(result);
      switch (this.key.value) {
      case KEY_VALUE.NAME:
        if (typeof parsed === 'string') {
          this.searchValue = parsed;
        } else if (typeof parsed === 'object' && 'name' in parsed) {
          this.searchValue = (parsed as any).name;
        } else {
          this.warning = 'Ugyldig QR kode format for søk på objektnavn. Mangler "name" felt.';
        }
        break;
      case KEY_VALUE.TFM_TAG:
        if (typeof parsed === 'object' && 'tfm_tag' in parsed) {
          this.searchValue = (parsed as any).tfm_tag;
        } else {
          this.warning = 'Ugyldig QR kode format for TFM tag søk. Mangler "tfm_tag" felt.';
        }
        break;
      case KEY_VALUE.RDS_TAG:
        if (typeof parsed === 'object' && 'rds_tag' in parsed) {
          this.searchValue = (parsed as any).rds_tag;
        } else {
          this.warning = 'Ugyldig QR kode format for RDS tag søk. Mangler "rds_tag" felt.';
        }
        break;
      default:
        this.warning = `Ugyldig søkenøkkel: ${this.key}. Gyldige nøkler er 'name', 'tfm_tag' eller 'rds_tag'.`;
      }

      if (!this.warning) {
        let object: AventiObject | undefined;
        switch (this.key.value) {
        case KEY_VALUE.NAME:
          object = this.objectsName
            ? this.objectsName.find(o => o.name === this.searchValue)
            : (await firstValueFrom(this.objectservice.getObjectByKey(this.projectId, 'name', this.searchValue)))[0];
          break;
        case KEY_VALUE.TFM_TAG:
          object = this.objectsTfm
            ? this.objectsTfm.find(o => o.tfmTag === this.searchValue)
            : (await firstValueFrom(this.objectservice.getObjectByKey(this.projectId, 'tfmTag', this.searchValue)))[0];
          break;
        case KEY_VALUE.RDS_TAG:
          object = this.objectsRds
            ? this.objectsRds.find(o => o.rdsTag === this.searchValue)
            : (await firstValueFrom(this.objectservice.getObjectByKey(this.projectId, 'rdsTag', this.searchValue)))[0];
          break;
        }

        if (object) {
          await this.router.navigate([`/fs/${this.projectId}/o/${object.id}`]);
        } else {
          switch (this.key.value) {
          case KEY_VALUE.NAME:
            this.warning = `Objekt med navn ${this.searchValue} finnes ikke i prosjekt ${this.projectId}.`;
            break;
          case KEY_VALUE.TFM_TAG:
            this.warning = `TFM tag ${this.searchValue} finnes ikke i prosjekt ${this.projectId}.`;
            break;
          case KEY_VALUE.RDS_TAG:
            this.warning = `RDS tag ${this.searchValue} finnes ikke i prosjekt ${this.projectId}.`;
            break;
          }
        }
      }
    } catch (e) {
      console.log('QR parsing error:', e);
      this.warning = 'QR-innholdet er ikke gyldig JSON.';
    }
  }
}
